import React from 'react';
import { Box, Grid, Typography, useTheme, Paper, Chip } from '@mui/material';
import { PersonAdd, Group, Wc, Category, VerifiedUser, GppBad } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';

const WidgetHeader = ({ title, icon: Icon }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {Icon && <Icon sx={{ color: theme.palette.text.secondary }} />}
            <Typography variant="h6" fontWeight="bold">
                {title}
            </Typography>
        </Box>
    );
};

const UserAnalytics = ({ data }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const labelColor = theme.palette.text.secondary;

    // Account Type Pie Chart
    const statusData = data?.status || [];
    // Aggregate by type
    const typeCounts = statusData.reduce((acc, curr) => {
        const type = curr._id.type || 'Unknown';
        acc[type] = (acc[type] || 0) + curr.count;
        return acc;
    }, {});
    
    const typeSeries = Object.values(typeCounts);
    const typeLabels = Object.keys(typeCounts);

    const typeOptions = {
        chart: { type: 'donut', background: 'transparent' },
        labels: typeLabels,
        colors: [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main],
        stroke: { show: false },
        dataLabels: { enabled: false },
        legend: { position: 'bottom', labels: { colors: labelColor } },
        tooltip: { theme: isDark ? 'dark' : 'light' },
        plotOptions: { pie: { donut: { size: '70%' } } }
    };

    // Gender Pie Chart
    const genderSeries = data?.gender?.map(item => item.count) || [];
    const genderLabels = data?.gender?.map(item => item._id || 'Not Specified') || [];

    const genderOptions = {
        chart: { type: 'pie', background: 'transparent' },
        labels: genderLabels,
        colors: ['#2196f3', '#e91e63', '#9e9e9e'],
        stroke: { show: false },
        dataLabels: { enabled: true, formatter: function (val, opts) { return opts.w.globals.labels[opts.seriesIndex] } },
        legend: { position: 'bottom', labels: { colors: labelColor } },
        tooltip: { theme: isDark ? 'dark' : 'light' }
    };

    // Registration Trend
    const regSeries = [{
        name: 'New Users',
        data: [data?.new?.today || 0, data?.new?.weekly || 0, data?.new?.monthly || 0]
    }];
    const regCategories = ['Today', 'past 7 Days', 'past 30 Days'];

    const regOptions = {
        chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
        xaxis: { categories: regCategories, labels: { style: { colors: labelColor } } },
        yaxis: { labels: { style: { colors: labelColor } } },
        colors: [theme.palette.success.main],
        grid: { borderColor: theme.palette.divider },
        tooltip: { theme: isDark ? 'dark' : 'light' }
    };

    return (
        <Box sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <WidgetHeader title="User Demographics & Growth" icon={Group} />
            
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Box elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Users</Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary">{data?.total?.toLocaleString()}</Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                     <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip icon={<PersonAdd />} label={`Today: ${data?.new?.today || 0}`} color="primary" variant="outlined" />
                        <Chip icon={<VerifiedUser />} label={`Verified: ${statusData.filter(s => s._id.verified).reduce((a,b)=>a+b.count,0)}`} color="success" variant="outlined" />
                     </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Account Types</Typography>
                        <Box sx={{ minHeight: 200 }}>
                            <ReactApexChart options={typeOptions} series={typeSeries} type="donut" height={200} />
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Gender Distribution</Typography>
                        <Box sx={{ minHeight: 200 }}>
                            <ReactApexChart options={genderOptions} series={genderSeries} type="pie" height={200} />
                        </Box>
                    </Box>
                </Grid>
                 <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">Recent Growth</Typography>
                        <Box sx={{ minHeight: 200 }}>
                            <ReactApexChart options={regOptions} series={regSeries} type="bar" height={200} />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserAnalytics;
